/**
 * Shared event date/time helpers — extracted from EventForm.tsx so the
 * EventDetail inline edit modal can use the same timezone-aware logic.
 *
 * Why: prior duplicate implementation in EventDetail.tsx used a naive
 * `iso.split("T")` that ignored the stored timezone. Combined with the
 * inline save handler's lack of required-time validation, that read-back
 * silently displayed times that didn't match what the operator entered
 * (Heather's 2 PM → 8 PM reproduction in `Data/SmartAdminReplacementUpdates.pdf`
 * R1 review). Centralizing the helpers ensures the EventForm and
 * EventDetail paths can't drift again.
 *
 * Audit-AI Sprint A Batch 1 A1 — GREEN with small revision.
 */

/**
 * Extract the local (timezone-adjusted) date portion of an ISO 8601
 * timestamp. Returns "YYYY-MM-DD" or "" when iso is missing/invalid.
 *
 * Falls back to the naive string split when:
 *   - no timezone is provided
 *   - the Intl APIs error out (older runtimes or malformed input)
 *
 * The fallback is intentionally tolerant of strings already in
 * wall-clock-local form like "2026-05-19T14:00:00-04:00" — the leading
 * date is the operator-entered date even without timezone math.
 */
export function getDateFromISO(iso?: string | null, tz?: string): string {
  if (!iso) {
    return "";
  }
  if (tz) {
    try {
      const date = new Date(iso);
      if (!isNaN(date.getTime())) {
        // en-CA emits the date portion as YYYY-MM-DD which matches the
        // <input type="date"> value contract.
        return new Intl.DateTimeFormat("en-CA", {
          timeZone: tz,
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).format(date);
      }
    } catch {
      // fall through
    }
  }
  return iso.split("T")[0];
}

/**
 * Extract the local (timezone-adjusted) time portion of an ISO 8601
 * timestamp in 24-hour HH:MM format. Returns "" when iso is missing.
 *
 * Same fallback rules as getDateFromISO. The 24-hour format matches
 * what <input type="time"> expects; the browser handles 12-hour display.
 */
export function getTimeFromISO(iso?: string | null, tz?: string): string {
  if (!iso) {
    return "";
  }
  if (tz) {
    try {
      const date = new Date(iso);
      if (!isNaN(date.getTime())) {
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone: tz,
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        }).formatToParts(date);
        const hour = parts.find((p) => p.type === "hour")?.value ?? "00";
        const minute = parts.find((p) => p.type === "minute")?.value ?? "00";
        return `${hour}:${minute}`;
      }
    } catch {
      // fall through
    }
  }
  const time = iso.split("T")[1];
  return time ? time.substring(0, 5) : "";
}

/**
 * DISPLAY the scheduled time of an event in the event's OWN timezone
 * (e.g. "2:00 PM"), regardless of the viewer's browser timezone.
 *
 * This is the wall-clock counterpart to formatters.formatTime (which renders
 * an instant in the VIEWER's local zone). Use this for event start_time /
 * end_time so a 2 PM Eastern event always reads "2:00 PM" next to its "EDT"
 * label, never converted to the viewer's zone — and never the "12:00 AM" the
 * old time-stripping formatter produced. Falls back to viewer-local when no
 * timezone is given. Returns "" for missing/invalid input.
 */
export function formatEventTime(iso?: string | null, tz?: string): string {
  if (!iso) {
    return "";
  }
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      return "";
    }
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz || undefined,
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    return "";
  }
}

/**
 * DISPLAY an event's scheduled DATE (or date range) in the event's OWN
 * timezone (e.g. "Aug 15, 2026" or "Aug 15 - Aug 16, 2026"), regardless of
 * the viewer's browser timezone.
 *
 * This is the date-range counterpart to formatters.formatDateRange, which
 * reads the literal Y-M-D out of the ISO string and therefore renders an
 * event whose end instant is (say) 2026-08-17T02:00Z — i.e. Aug 16 9 PM
 * Central — as "Aug 17". For an event's SCHEDULED window the day must be
 * derived in the event's timezone, exactly like formatEventTime does for the
 * time. Collapses to a single date when start and end fall on the same
 * event-local day, and drops the repeated year for a same-year range. Falls
 * back to viewer-local when no timezone is given; returns "No date" when the
 * start is missing.
 */
export function formatEventDateRange(
  startIso?: string | null,
  endIso?: string | null,
  tz?: string,
): string {
  if (!startIso) {
    return "No date";
  }
  const dateFmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz || undefined,
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
  // en-CA yields YYYY-MM-DD, a stable key for same-day / same-year comparison
  // in the event's timezone (not the viewer's, and not the raw UTC string).
  const keyFmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz || undefined,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  try {
    const start = new Date(startIso);
    if (isNaN(start.getTime())) {
      return "No date";
    }
    const startFull = dateFmt.format(start);
    if (!endIso) {
      return startFull;
    }
    const end = new Date(endIso);
    if (isNaN(end.getTime())) {
      return startFull;
    }
    const startKey = keyFmt.format(start);
    const endKey = keyFmt.format(end);
    if (startKey === endKey) {
      return startFull;
    }
    const endFull = dateFmt.format(end);
    // Same event-local year → drop the year from the start ("Aug 15 - Aug 16, 2026").
    if (startKey.slice(0, 4) === endKey.slice(0, 4)) {
      const startNoYear = new Intl.DateTimeFormat("en-US", {
        timeZone: tz || undefined,
        month: "short",
        day: "2-digit",
      }).format(start);
      return `${startNoYear} - ${endFull}`;
    }
    return `${startFull} - ${endFull}`;
  } catch {
    return "No date";
  }
}

/**
 * DISPLAY an event's scheduled date + time in the event's OWN timezone
 * (e.g. "May 19, 2026, 2:00 PM"). Wall-clock counterpart to
 * formatters.formatDateTime. Returns "" for missing/invalid input.
 */
export function formatEventDateTime(iso?: string | null, tz?: string): string {
  if (!iso) {
    return "";
  }
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      return "";
    }
    return new Intl.DateTimeFormat("en-US", {
      timeZone: tz || undefined,
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    }).format(d);
  } catch {
    return "";
  }
}

/**
 * The Central zone used by the Events list "show times in Central" toggle.
 * IANA rather than a fixed offset so CDT/CST is chosen per date.
 */
export const CENTRAL_TZ = "America/Chicago";

/**
 * The short zone label for a given INSTANT in a given zone — "EDT" / "EST",
 * "CDT" / "CST", and so on.
 *
 * Derived at the event's own instant, deliberately. `formatters.formatTimezone`
 * takes only a zone name and reads the label off `new Date()`, so a January
 * event renders "EST" in winter and "EDT" in summer purely because of when the
 * page is loaded. The label has to come from the timestamp being displayed.
 *
 * Returns "" for missing/invalid input, and falls back to the raw zone name if
 * Intl declines to emit a label so the caller never shows a bare time it
 * cannot attribute to a zone.
 */
export function getEventZoneAbbrev(iso?: string | null, tz?: string): string {
  if (!iso) {
    return "";
  }
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      return "";
    }
    const label = new Intl.DateTimeFormat("en-US", {
      timeZone: tz || undefined,
      timeZoneName: "short",
    })
      .formatToParts(d)
      .find((p) => p.type === "timeZoneName")?.value;
    return label || tz || "";
  } catch {
    return "";
  }
}

/**
 * DISPLAY an event's scheduled time with its zone label — "3:00 PM EDT".
 *
 * `displayTz` overrides the zone the time is rendered in, which is how the
 * Events list converts every row to Central: pass CENTRAL_TZ and a 3 PM
 * Eastern event reads "2:00 PM CDT". Omit it and the event renders in its own
 * zone, which is the default.
 *
 * Callers converting to another zone MUST also derive the displayed DATE in
 * that zone (formatEventDateRange takes the same tz). A 12:30 AM Eastern event
 * is 11:30 PM Central on the PREVIOUS day, so a converted time beside an
 * unconverted date is simply wrong.
 */
export function formatEventTimeWithZone(
  iso?: string | null,
  tz?: string,
  displayTz?: string,
): string {
  const zone = displayTz || tz;
  const time = formatEventTime(iso, zone);
  if (!time) {
    return "";
  }
  const abbrev = getEventZoneAbbrev(iso, zone);
  return abbrev ? `${time} ${abbrev}` : time;
}
